/* eslint-disable react/no-array-index-key */
/* eslint-disable no-unused-expressions */
/* eslint-disable max-len */
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Delayed from '@assets/delayed.svg';
import Damaged from '@assets/damaged.svg';
import Lost from '@assets/lost.svg';
import Return from '@assets/return.svg';
import Wrong from '@assets/something-wrong.svg';
import { useBehaviorData } from '@hooks';

import { ClaimOption } from '@components';
import {
  ClaimType, ErrorCode, RAPolicyType, EmailAddress, ClaimTypeText, PLATFORM_NAME, NEED_TO_ADD_TRACKING_NUMBER,
  ErrorCodeToSpecialText,
} from '@constants';
import {
  setClaimInfo,
  setPackageList, clearSelectedItems, setDelayPackageId,
} from '@store';
import {
  getPackage,
} from '@api';
import { logError } from '@log';
import { whiteList } from './whiteList';
import classes from './index.module.css';

const SUB_TITLE = 'Please tell us what happened to your order?';

const useOptionList = (firstName) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const setReportData = useBehaviorData();
  const {
    policyList, platform, specialPlatform, email,
  } = useSelector((state) => state.policy);

  const isGarmentory = platform === PLATFORM_NAME.GARMENTORY;
  const delayIssueName = isGarmentory ? ClaimType.Theft : ClaimType.Delay;

  const onClickContinue = async (type, policy) => {
    dispatch(setClaimInfo({ type, ...policy }));
    setReportData({ optionsTime: `${Date.now()}` });

    try {
      const isNeedSpecial = NEED_TO_ADD_TRACKING_NUMBER.includes(specialPlatform);
      const time = (Date.now() - (policy.insureTimeStamp || 0)) / 60 / 60 / 1000 / 24;
      // console.log(time, 'time', isNeedSpecial, 'isNeedSpecial');
      // shopline/ctbids bp 特殊逻辑， 48小时内，前端提示报错
      if (isNeedSpecial) {
        // pending & 48以内 报错
        if (policy.status === 'pending' && time < 2 && !whiteList.includes(email)) {
          navigate('../error', { state: { code: ErrorCode.Instant } });
          return;
        }

        // ctbids GDD特有逻辑 29天后，如果还是pending 则直接报错
        if (type !== ClaimType.RA && platform === PLATFORM_NAME.CTBIDS && policy.status === 'pending' && time >= 29) {
          navigate('../error', { state: { code: ErrorCode.SpecialCodeForCTBids } });
          return;
        }
      }

      const resp = await getPackage({ policyId: policy.policyId, issueType: type });
      const { code, data } = resp;
      // TODO 需要判断backStep
      // const backStep = RAPolicyType[type];
      if (code === 0) {
        dispatch(setPackageList(data));
        dispatch(clearSelectedItems());
        dispatch(setDelayPackageId());

        // 3min空档期重复报案，直接出3分钟提示页
        if (isNeedSpecial && policy.status === 'pending' && data?.fulfilledItems?.length) {
          navigate('../success');
          return;
        }
        // shopline 特殊逻辑， 48小时外保单未激活，前端提示报错 跳转fulfill 填写tracknumber
        if (isNeedSpecial && policy.status === 'pending' && time > 2) {
          navigate('../unfulfilled');
          return;
        }
        // ctbids GDD 特有的逻辑 29天后如果订单激活了，没有trackingNumber，同样直接报错
        if (type !== ClaimType.RA && platform === PLATFORM_NAME.CTBIDS && time >= 29 && !data.fulfilledItems[0]?.trackingNumber) {
          navigate('../error', { state: { code: ErrorCode.SpecialCodeForCTBids } });
          return;
        }
        navigate('../choose');
      } else if (ErrorCodeToSpecialText.includes(code)) {
        navigate('../error', { state: { code, type } });
      } else {
        navigate('../error', { state: { code: ErrorCode.Wrong } });
      }
      // getPackageData({ policyId: policy.policyId, issueType: type }, dispatch, navigate);
    } catch (e) {
      logError(e);
    }
  };
  const onClickSendEmail = (orderNumber, policy) => {
    const a = document.createElement('a');
    if (policy === PLATFORM_NAME.POIZON) {
      a.href = `mailto:${EmailAddress.Poizon_support}?cc=${EmailAddress.Seel}&subject=Delayed fulfillment of ${orderNumber || ''}&body=${encodeURIComponent(`Hi POIZON Support,\n\nMy order ${orderNumber || ''} was not fulfilled within the guaranteed 9 days.\nCould you help handle the issue?\n\nThanks,\n${firstName || ''}`)}`;
      a.innerHTML = EmailAddress.Poizon_support;
    } else {
      // mailto body encodeURIComponent+\n 或者直接%0A皆可实现换行，但不支持html格式，故不能加粗斜体颜色
      a.href = `mailto:${EmailAddress.Garmentory_support}?subject=Issue with Order ${orderNumber || ''}&body=${encodeURIComponent(`Hi Garmentory Support,\n\nWhen I received my order ${orderNumber || ''}, I noticed [explain the issue in detail, for instance wrong item, missing item(s), damaged item, etc. and provide photos if possible]\n\nThanks,\n${firstName}`)}`;
      a.innerHTML = EmailAddress.Garmentory_support;
    }
    a.click();
  };

  const getItemIssue = (item, code) => {
    const issueMap = {
      [ClaimType.RA]: {
        count: 10,
        id: 'RA',
        icon: Return,
        title: isGarmentory ? "I don't like the item(s), or I don't want it anymore" : 'I want to return the item(s)',
        onClick: () => onClickContinue(ClaimType.RA, item),
      },
      [ClaimType.Theft]: {
        count: 20,
        id: 'theft',
        icon: Delayed,
        title: ClaimTypeText[ClaimType.Theft],
        hoverText: isGarmentory ? 'NOTE: Porch piracy must be reported within 7 days after all packages in this order are delivered.' : '',
        onClick: () => onClickContinue(ClaimType.Theft, item),
      },
      [ClaimType.Delay]: {
        count: 30,
        id: 'delay',
        icon: Delayed,
        title: ClaimTypeText[ClaimType.Delay],
        hoverText: platform === PLATFORM_NAME.POIZON ? 'NOTE: Issues of delay must be reported after 10 days of fulfillment.' : '',
        onClick: () => onClickContinue(ClaimType.Delay, item),
      },
      [ClaimType.Damage]: {
        count: 40,
        id: 'damage',
        icon: Damaged,
        title: ClaimTypeText[ClaimType.Damage],
        hoverText: 'NOTE: Issues of damage must be reported within 7 days after the delivery date.',
        onClick: () => onClickContinue(ClaimType.Damage, item),
      },
      [ClaimType.Loss]: {
        count: 50,
        id: 'loss',
        icon: Lost,
        title: ClaimTypeText[ClaimType.Loss],
        onClick: () => onClickContinue(ClaimType.Loss, item),
      },
      [ClaimType.AD]: {
        count: 50,
        id: 'ad',
        icon: '',
        title: ClaimTypeText[ClaimType.AD],
        onClick: () => onClickContinue(ClaimType.AD, item),
      },
      [ClaimType.EW]: {
        count: 60,
        id: 'ew',
        icon: '',
        title: ClaimTypeText[ClaimType.EW],
        onClick: () => onClickContinue(ClaimType.EW, item),
      },
    };
    return issueMap[code];
  };
  const list = policyList.map((policy) => {
    const {
      policyType, orderNumber, liabilities = [],
    } = policy;
    const options = [];
    if (liabilities.length) {
      // 如果接口返回liabilities，取接口返回责任list 否则根据policyType判断 兼容老逻辑
      liabilities.forEach(({ code }) => {
        const item = getItemIssue(policy, code);
        item && options.push(item);
      });
    } else if (RAPolicyType.includes(policyType)) {
      if (isGarmentory) {
        options.push({
          count: 9,
          id: 'seller',
          icon: Wrong,
          title: "There's something wrong with my order",
          onClick: () => onClickSendEmail(orderNumber),
        });
      }
      options.push({
        count: 10,
        id: 'RA',
        icon: Return,
        title: isGarmentory ? "I don't like the item(s), or I don't want it anymore" : 'I want to return the item(s)',
        onClick: () => onClickContinue(ClaimType.RA, policy),
      });

      return options;
    } else {
      // if (platform === PLATFORM_NAME.POIZON) {
      //   options.push({
      //     count: 8,
      //     id: 'poizon_email',
      //     icon: Wrong,
      //     title: 'My order was not fulfilled within 9 days',
      //     onClick: () => onClickSendEmail(orderNumber, PLATFORM_NAME.POIZON),
      //   });
      // }

      options.push(
        {
          count: 30,
          id: 'delay',
          icon: Delayed,
          title: isGarmentory ? ClaimTypeText[ClaimType.Theft] : ClaimTypeText[ClaimType.Delay],
          onClick: () => onClickContinue(delayIssueName, policy),
        },
        {
          count: 40,
          id: 'damage',
          icon: Damaged,
          title: ClaimTypeText[ClaimType.Damage],
          hoverText: 'NOTE: Issues of damage must be reported within 7 days after the delivery date.',
          onClick: () => onClickContinue(ClaimType.Damage, policy),
        },
        {
          count: 50,
          id: 'lost',
          icon: Lost,
          title: ClaimTypeText[ClaimType.Loss],
          onClick: () => onClickContinue(ClaimType.Loss, policy),
        },
      );
    }
    return options.sort((a, b) => a.count - b.count);
  }).flat();

  return list;
};

export const Options = () => {
  const {
    firstName, platform,
  } = useSelector((state) => state.policy);
  const optionList = useOptionList(firstName);
  return (
    <section className={classes.content}>
      <section className={classes.title}>
        Hi
        <section className={classes.customerName}>{platform === PLATFORM_NAME.DHGATE ? '' : firstName}</section>
        !
      </section>
      <section className={classes.sub_title}>{SUB_TITLE}</section>
      {optionList.map((option, index) => (
        <ClaimOption
          key={option.id + index}
          icon={option.icon}
          title={option.title}
          hoverText={option.hoverText}
          onClick={option.onClick}
        />
      ))}
    </section>
  );
};
